/**
 * Utility functions for the business model strategy
 */

import { runAIMatches, runMultiMatches, fetchQueries } from '../../../utils/api';
import { COMPANY_STAGES } from './constants';
import { fetchQueryByCategory } from '../utils_common';

/**
 * Generate business model data from API
 * @param {Array} messages - Current conversation messages
 * @param {Function} setMessages - Function to update messages state
 * @param {Object} decision - Current decision object
 * @param {boolean} useCache - Whether to use cached data
 * @param {Array} providedQueries - Optional queries from AppLayout/useMessaging
 * @returns {Promise<Object>} The business model data
 */
export const generateBusinessModelData = async (messages, setMessages, decision, useCache = true, providedQueries = null) => {
  // Fetch queries if not provided
  let queries = providedQueries;
  if (!queries) {
    try {
      queries = await fetchQueries();
      console.log('Fetched queries for business model data');
    } catch (error) {
      console.error('Error fetching queries:', error);
    }
  }
  let query_set = fetchQueryByCategory('BIZMODEL');

  // Update thinking accordion
  if (setMessages) {
    setMessages(prev => {
      const updatedMessages = prev.map(msg =>
        msg.role === 'AI' && msg.content === 'Typing...'
          ? {
              ...msg,
              thinking: `${msg.thinking}\nGenerating business model data...`
            }
          : msg
      );
      return updatedMessages;
    });
  }

  try {
    // First get the regular AI matches to extract requirements
    const regularMatches = await runAIMatches(messages);
    if (!regularMatches) {
      throw new Error('Failed to get AI matches');
    }

    // Call setMatchResults if available in the decision object
    if (decision && decision.setMatchResults) {
      console.log('Calling setMatchResults with regularMatches');
      decision.setMatchResults(regularMatches);
    }

    // Update match results in the sidebar if handleRefreshResults is provided
    if (decision && decision.handleRefreshResults) {
      console.log('Updating match results in sidebar with handleRefreshResults');
      decision.handleRefreshResults(messages, regularMatches);
    }
    
    // Create an array of just hex values for the stage queries
    const bmQueries = [];
    const stageMapping = {}; // Map to track which hex value corresponds to which stage key
    
    Object.keys(query_set).forEach((k)=> {
      bmQueries = bmQueries.concat(query_set[k]);
    })
    let query_det = {};
    bmQueries.forEach((bm)=> {
      query_det[bm] = queries[bm];
    });

    // Extract requirements from regularMatches
    const requirements =
      regularMatches.search_gen_response &&
      regularMatches.search_gen_response.icp_requirements
        ? regularMatches.search_gen_response.icp_requirements
        : [];
    
    console.log('Requirements count:', requirements.length);
    
    // Run multi matches to get the distribution
    if (requirements.length > 0 && bmQueries.length > 0) {
      console.log('Running multiMatches with requirements and bmQueries');
      const multiMatchesResult = await runMultiMatches(
        requirements,
        bmQueries,
        [],
        {run_on_self_org: true}
      );
      
      // Process the results
      if (multiMatchesResult && multiMatchesResult.count_by_queries) {
        const bmData = {};
        
        // Store the base_filter_count for percentage calculations
        const totalCount = multiMatchesResult.base_filter_count || 0;
        
        // Map the counts to the stage keys using the stageMapping
        Object.entries(multiMatchesResult.count_by_queries).forEach(([hexValue, count]) => {
          bmData[hexValue] = count;
        });
        
        console.log('Generated stageData:', bmData);
        console.log('Total count (base_filter_count):', totalCount);
        bmData.totalCount = totalCount;
        bmData.queryDet = query_det;
        bmData.querySet = query_set;

        // If we have a setMatchResults function in the decision, update the matchResults
        if (decision.setMatchResults) {
          const updatedMatchResults = {
            ...decision.matchResults,
            businessModelData: bmData,
            businessModelTotal: totalCount
          };
          decision.setMatchResults(updatedMatchResults);
        }

        return {
          businessModelData: bmData,
          totalCount,
          aiMatchesResult: {
            ...regularMatches,
            businessModelData: bmData,
            businessModelTotal: totalCount
          }
        };
      }
    }
  } catch (error) {
    console.error('Error generating business model data:', error);
  }
};
